import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import {
  LayoutGrid,
  Sparkles,
  Check,
  Clock3,
  BarChart3,
  Shield,
  Lock,
} from 'lucide-react';
import * as api from '../../api/client';
import Reveal from '../../components/Reveal';
import Seo from '../../components/Seo';
import { buildLandingJsonLd } from '../../seo/landingSchemas';
import type { LocaleCode } from '../../config/i18n';
import HeroProductDemo from '../components/HeroProductDemo';

const STEP_COLORS = ['text-lc-pink', 'text-lc-purple', 'text-lc-orange', 'text-lc-pink'] as const;

const FEATURES = [
  { key: 'multiAccount', icon: LayoutGrid, iconBg: 'bg-[#3a1a2a]', iconColor: 'text-lc-pink' },
  { key: 'aiStyle', icon: Sparkles, iconBg: 'bg-[#3a2418]', iconColor: 'text-lc-orange' },
  { key: 'humanControl', icon: Check, iconBg: 'bg-[#3a1a2a]', iconColor: 'text-lc-pink' },
  { key: 'smartSchedule', icon: Clock3, iconBg: 'bg-[#2a1f3d]', iconColor: 'text-lc-purple' },
  { key: 'analytics', icon: BarChart3, iconBg: 'bg-[#2a1f3d]', iconColor: 'text-lc-purple' },
] as const;

const FAQ_KEYS = ['password', 'credits', 'multiAccount', 'autoPost'] as const;

const AVATAR_COLORS = ['bg-lc-pink', 'bg-lc-orange', 'bg-lc-purple'] as const;

const SCATTER_LAYOUT = [
  { left: '1%', top: '4%', rot: -6, delay: '0s' },
  { left: '76%', top: '2%', rot: 4, delay: '0.45s' },
  { left: '4%', top: '62%', rot: -3, delay: '0.9s' },
  { left: '80%', top: '58%', rot: 7, delay: '1.35s' },
  { left: '30%', top: '78%', rot: -5, delay: '1.8s' },
  { left: '56%', top: '82%', rot: 3, delay: '2.25s' },
  { left: '18%', top: '22%', rot: 5, delay: '2.7s' },
  { left: '66%', top: '24%', rot: -4, delay: '3.15s' },
  { left: '40%', top: '4%', rot: 6, delay: '3.6s' },
  { left: '88%', top: '30%', rot: -7, delay: '4.05s' },
  { left: '8%', top: '40%', rot: 2, delay: '4.5s' },
  { left: '46%', top: '90%', rot: -2, delay: '4.95s' },
] as const;

function SectionEyebrow({ children, color = 'text-lc-pink' }: { children: React.ReactNode; color?: string }) {
  return <div className={`mb-3 text-[12.5px] font-semibold ${color}`}>{children}</div>;
}

export default function Home() {
  const { t, i18n } = useTranslation();
  const [plans, setPlans] = useState<api.Plan[]>([]);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const locale: LocaleCode = i18n.language === 'en' ? 'en' : 'tr';

  useEffect(() => {
    api.listPlans().then(setPlans).catch(() => {});
  }, []);

  const featured = t('home.testimonials.featured', { returnObjects: true }) as Array<{
    quote: string;
    name: string;
    role: string;
  }>;
  const scatter = t('home.testimonials.scatter', { returnObjects: true }) as Array<{
    quote: string;
    name: string;
  }>;

  const faqItems = useMemo(
    () => FAQ_KEYS.map((key) => ({ q: t(`home.faq.${key}.q`), a: t(`home.faq.${key}.a`) })),
    [t]
  );

  const jsonLd = useMemo(
    () =>
      buildLandingJsonLd({
        locale,
        title: t('seo.home.title'),
        description: t('seo.home.description'),
        keywords: t('seo.home.keywords'),
        faqs: faqItems,
      }),
    [locale, t, faqItems]
  );

  return (
    <>
      <Seo
        title={t('seo.home.title')}
        description={t('seo.home.description')}
        keywords={t('seo.home.keywords')}
        locale={locale}
        canonicalPath="/"
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section
        aria-labelledby="hero-heading"
        className="relative mx-auto max-w-[1180px] px-6 pb-[60px] pt-[88px] text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-7 inline-flex items-center gap-2 rounded-full border border-lc-borderStrong bg-lc-surface px-3.5 py-1.5 text-[12.5px] font-medium text-lc-orange"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-lc-pink animate-blink" />
          {t('home.badge')}
        </motion.div>

        <motion.h1
          id="hero-heading"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mx-auto mb-[22px] max-w-[880px] text-4xl font-extrabold leading-[1.08] tracking-tight sm:text-5xl lg:text-[56px]"
        >
          {t('home.heroTitlePre')}
          <br />
          {t('home.heroTitleMid')}{' '}
          <span className="text-lc-pink">{t('home.heroTitleEmphasis')}</span>.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="mx-auto mb-9 max-w-[620px] text-lg leading-relaxed text-lc-muted"
        >
          {t('home.heroSubtitle')}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.18 }}
          className="mb-5 text-[12.5px] font-medium text-lc-faint"
        >
          {t('home.heroPlatformNote')}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 0.22 }}
          className="mb-5 flex flex-wrap items-center justify-center gap-3.5"
        >
          <Link to="/register" className="btn-lc px-[26px] py-3.5 text-[15.5px]">
            {t('home.heroCta')}
          </Link>
          <a href="#how" className="btn-lc-ghost px-[26px] py-3.5 text-[15.5px]">
            {t('home.heroSecondaryCta')}
          </a>
        </motion.div>

        <p className="mb-14 text-[12.5px] font-medium text-lc-faint">{t('home.heroDisclaimer')}</p>

        <div className="mb-14 flex flex-wrap items-center justify-center gap-7 text-[13px] text-lc-muted">
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-lc-pink" strokeWidth={2} />
            {t('home.trust1')}
          </div>
          <div className="flex items-center gap-2">
            <Lock className="h-4 w-4 text-lc-purple" strokeWidth={2} />
            {t('home.trust2')}
          </div>
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-lc-orange" strokeWidth={2} />
            {t('home.trust3')}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mx-auto max-w-[860px]"
        >
          <HeroProductDemo />
        </motion.div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" aria-labelledby="how-heading" className="mx-auto max-w-[1180px] px-6 py-[100px]">
        <Reveal>
          <div className="mb-16 text-center">
            <SectionEyebrow>{t('home.eyebrowHow')}</SectionEyebrow>
            <h2 id="how-heading" className="text-[32px] font-extrabold tracking-tight sm:text-[38px]">
              {t('home.titleHow')}
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-0">
          {[0, 1, 2, 3].map((i) => (
            <Reveal key={i} delay={i * 0.08} className="relative px-0 lg:px-5">
              <div className="mb-[18px] flex items-center gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-lc-borderStrong bg-lc-surface text-[15px] font-bold ${STEP_COLORS[i]}`}
                >
                  {String(i + 1).padStart(2, '0')}
                </div>
                {i < 3 && <div className="hidden h-px flex-1 bg-lc-border lg:block" />}
              </div>
              <h3 className="mb-2 min-h-[42px] text-[17px] font-bold leading-snug">
                {t(`home.steps.${i}.title`)}
              </h3>
              <p className="text-sm leading-relaxed text-lc-muted">{t(`home.steps.${i}.desc`)}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" aria-labelledby="features-heading" className="mx-auto max-w-[1180px] px-6 pb-[100px] pt-10">
        <Reveal>
          <div className="mb-14 text-center">
            <SectionEyebrow color="text-lc-orange">{t('home.eyebrowFeatures')}</SectionEyebrow>
            <h2 id="features-heading" className="text-[32px] font-extrabold tracking-tight sm:text-[38px]">
              {t('home.titleFeatures')}
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.key} delay={i * 0.06}>
              <div className="h-full rounded-xl border border-lc-border bg-lc-card p-7 transition-colors hover:border-lc-borderStrong hover:bg-lc-cardHover">
                <div className={`mb-[18px] flex h-[38px] w-[38px] items-center justify-center rounded-lg ${f.iconBg}`}>
                  <f.icon className={`h-[19px] w-[19px] ${f.iconColor}`} strokeWidth={2} />
                </div>
                <h3 className="mb-2 text-[16.5px] font-bold">{t(`home.features.${f.key}.title`)}</h3>
                <p className="text-sm leading-relaxed text-lc-muted">{t(`home.features.${f.key}.desc`)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonials" aria-labelledby="testimonials-heading" className="mx-auto max-w-[1180px] px-6 pb-[110px] pt-10">
        <Reveal>
          <div className="mb-11 text-center">
            <SectionEyebrow color="text-lc-orange">{t('home.eyebrowTestimonials')}</SectionEyebrow>
            <h2 id="testimonials-heading" className="text-[32px] font-extrabold tracking-tight sm:text-[38px]">
              {t('home.titleTestimonials')}
            </h2>
          </div>
        </Reveal>

        <div className="relative flex min-h-[360px] items-center justify-center sm:min-h-[400px]">
          {Array.isArray(scatter) &&
            scatter.map((sc, i) => {
              const layout = SCATTER_LAYOUT[i % SCATTER_LAYOUT.length];
              return (
                <div
                  key={`${sc.name}-${i}`}
                  className="pointer-events-none absolute z-[1] hidden w-40 rounded-[10px] border border-lc-borderStrong bg-lc-card px-3 py-2.5 opacity-85 animate-floatY lg:block"
                  style={
                    {
                      left: layout.left,
                      top: layout.top,
                      animationDelay: layout.delay,
                      '--r': `${layout.rot}deg`,
                    } as React.CSSProperties
                  }
                >
                  <p className="mb-1.5 text-[11.5px] leading-snug text-[#e6dbe9]">“{sc.quote}”</p>
                  <div className="text-[10.5px] font-semibold text-lc-muted">{sc.name}</div>
                </div>
              );
            })}

          <div className="relative z-[2] flex w-full flex-wrap items-center justify-center gap-[22px]">
            {Array.isArray(featured) &&
              featured.map((tm, i) => (
                <Reveal key={tm.name} delay={i * 0.1}>
                  <div className="w-full max-w-[270px] rounded-xl border border-lc-pink bg-lc-surface px-5 py-[18px] shadow-[0_20px_50px_-25px_#000]">
                    <p className="mb-3 text-[13.5px] leading-relaxed text-lc-text">“{tm.quote}”</p>
                    <div className="flex items-center gap-2.5">
                      <div
                        className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full text-xs font-bold text-lc-ink ${AVATAR_COLORS[i % AVATAR_COLORS.length]}`}
                      >
                        {tm.name[0]}
                      </div>
                      <div>
                        <div className="text-[12.5px] font-semibold text-lc-text">{tm.name}</div>
                        <div className="text-[11px] text-lc-muted">{tm.role}</div>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
          </div>
        </div>

        <div className="relative z-[5] mt-9 text-center">
          <Link to="/register" className="btn-lc inline-flex px-[26px] py-3 text-[14.5px]">
            {t('home.testimonialsCta')}
          </Link>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" aria-labelledby="pricing-heading" className="mx-auto max-w-[1180px] px-6 pb-[110px] pt-10">
        <Reveal>
          <div className="mb-4 text-center">
            <SectionEyebrow color="text-lc-purple">{t('home.eyebrowPricing')}</SectionEyebrow>
            <h2 id="pricing-heading" className="mb-3.5 text-[32px] font-extrabold tracking-tight sm:text-[38px]">
              {t('home.titlePricing')}
            </h2>
            <p className="mb-14 text-[15.5px] text-lc-muted">{t('home.subtitlePricing')}</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 lg:items-stretch">
          {plans.map((p, i) => {
            const popular = i === 1;
            return (
              <Reveal key={p.id} delay={i * 0.08} className="h-full">
                <div
                  className={`relative flex h-full flex-col rounded-[14px] px-[26px] py-[30px] ${
                    popular
                      ? 'border border-lc-pink bg-lc-surface'
                      : 'border border-lc-border bg-lc-card'
                  }`}
                >
                  {popular && (
                    <div className="absolute -top-[13px] left-[26px] rounded-[5px] bg-lc-pink px-2.5 py-1 text-[11px] font-bold text-lc-ink">
                      {t('home.popular')}
                    </div>
                  )}
                  <div className="mb-1.5 text-[15px] font-bold">{p.name}</div>
                  <div className="mb-5 flex items-baseline gap-1">
                    <span className="text-[34px] font-extrabold tracking-tight">${p.monthlyPriceUsd}</span>
                    <span className="text-[13px] text-lc-faint">{t('home.perMonth')}</span>
                  </div>
                  <div className="mb-[22px] text-[12.5px] font-medium text-lc-orange">
                    {t('home.planMeta', { accounts: p.maxInstagramAccounts, credits: p.monthlyAiCredits })}
                  </div>
                  <div className="mb-6 flex flex-1 flex-col gap-[11px]">
                    <div className="flex items-start gap-2.5 text-[13.5px] leading-snug text-lc-soft">
                      <Check className="mt-0.5 h-[15px] w-[15px] shrink-0 text-lc-pink" strokeWidth={2.5} />
                      {t('home.igAccounts', { count: p.maxInstagramAccounts })}
                    </div>
                    <div className="flex items-start gap-2.5 text-[13.5px] leading-snug text-lc-soft">
                      <Check className="mt-0.5 h-[15px] w-[15px] shrink-0 text-lc-pink" strokeWidth={2.5} />
                      {t('home.monthlyCredits', { count: p.monthlyAiCredits })}
                    </div>
                  </div>
                  <Link
                    to="/register"
                    className={`block rounded-[7px] py-[11px] text-center text-sm font-bold ${
                      popular
                        ? 'bg-lc-pink text-lc-ink hover:bg-lc-pinkHover'
                        : 'border border-lc-borderStrong bg-transparent text-lc-text hover:border-lc-purple'
                    }`}
                  >
                    {t('home.planCta')}
                  </Link>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-col items-start justify-between gap-5 rounded-[14px] border border-lc-border bg-lc-card px-[26px] py-[28px] sm:flex-row sm:items-center">
            <div>
              <div className="mb-1 text-[15px] font-bold">{t('home.freeTaste.name')}</div>
              <div className="mb-2 flex items-baseline gap-1">
                <span className="text-[28px] font-extrabold tracking-tight">$0</span>
                <span className="text-[13px] text-lc-faint">{t('home.freeTaste.priceNote')}</span>
              </div>
              <p className="max-w-xl text-[13.5px] leading-snug text-lc-muted">{t('home.freeTaste.desc')}</p>
            </div>
            <Link
              to="/free-taste"
              className="shrink-0 rounded-[7px] border border-lc-borderStrong bg-transparent px-5 py-[11px] text-center text-sm font-bold text-lc-text hover:border-lc-orange"
            >
              {t('home.freeTaste.cta')}
            </Link>
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section id="faq" aria-labelledby="faq-heading" className="mx-auto max-w-[760px] px-6 pb-[110px] pt-10">
        <Reveal>
          <div className="mb-12 text-center">
            <SectionEyebrow>{t('home.eyebrowFaq')}</SectionEyebrow>
            <h2 id="faq-heading" className="text-[32px] font-extrabold tracking-tight sm:text-[38px]">
              {t('home.titleFaq')}
            </h2>
          </div>
        </Reveal>
        <div className="flex flex-col gap-2.5">
          {FAQ_KEYS.map((key, i) => {
            const isOpen = openFaq === i;
            return (
              <Reveal key={key} delay={i * 0.05}>
                <div className="overflow-hidden rounded-[10px] border border-lc-border bg-lc-card">
                  <button
                    type="button"
                    onClick={() => setOpenFaq(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 px-5 py-[18px] text-left text-[15px] font-semibold text-lc-text"
                  >
                    {t(`home.faq.${key}.q`)}
                    <span className="shrink-0 text-lg font-medium text-lc-muted">{isOpen ? '−' : '+'}</span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                        className="overflow-hidden"
                      >
                        <p className="px-5 pb-5 text-sm leading-relaxed text-lc-muted">
                          {t(`home.faq.${key}.a`)}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1180px] px-6 pb-[110px]">
        <Reveal>
          <div className="rounded-[18px] border border-lc-borderStrong bg-gradient-to-br from-lc-surface to-lc-bg px-8 py-[70px] text-center sm:px-10">
            <h2 className="mb-4 text-[28px] font-extrabold tracking-tight sm:text-[34px]">{t('home.ctaTitle')}</h2>
            <p className="mb-[30px] text-base text-lc-muted">{t('home.ctaDesc')}</p>
            <Link to="/register" className="btn-lc inline-flex px-[30px] py-3.5 text-[15.5px]">
              {t('home.ctaButton')}
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
